import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Table, TableBody, TableContainer, TableRow} from "@mui/material";
import {FinancialFilters} from "./financialFilters";
import {PerCompanyBar} from "./perCompanyBar";
import {TargetVsActualBar} from "./targetVsActualBar";
import {TargetVsActualVertical} from "./targetVsActualVertical";
import {TargetVsActualDoubleBar} from "./TargetVsActualDoubleBar";
import {useSitStores} from "../../../stores";
import type {ColSpec} from "../../../components/table/MithraTableHeadColumns";
import {MithraTableHeadColumns} from "../../../components/table/MithraTableHeadColumns";
import {MithraTableCell} from "../../../components/table/MithraTableCell";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import i18n from "../../../i18n/i18n";

const fin = (k: string) => i18n.t(`fin.${k}`, {ns: 'sit'});

export const FinancialDashboard: React.FC = observer(() => {
    const {fbStore} = useSitStores();
    const [col_1, setCol1] = useState(0);
    const [col_2, setCol2] = useState(0);
    const [col_3, setCol3] = useState(0);
    const [col_4, setCol4] = useState(0);
    const columns: ColSpec[] = [
        {cls: 'col-cluster', txt: fin('cluster')},
        {cls: 'col-savings_projected', txt: fin('savingsProjected')},
        {cls: 'col-cost_avoidance', txt: fin('costAvoidance')},
        {cls: 'col-savings_realized', txt: fin('savingsRealized')},
        {cls: 'col-target', txt: fin('target')},
    ]

    useEffect(() => {
        if (fbStore.targetDataPerCluster && fbStore.targetDataPerCluster.length > 0) {
            setCol1(fbStore.targetDataPerCluster.map(c => c.clusterId).reduce((total, clusterId) => {
                return total + (
                    fbStore.contributionPerClusterCumSumSavings?.find(c => c.clusterId === clusterId)?.data.find(dataPoint => dataPoint.month.getMonth() === 11)?.value ?? 0
                );
            }, 0));
            setCol2(fbStore.targetDataPerCluster.map(c => c.clusterId).reduce((total, clusterId) => {
                return total + (
                    fbStore.contributionPerClusterCumSumAvoidance?.find(c => c.clusterId === clusterId)?.data.find(dataPoint => dataPoint.month.getMonth() === 11)?.value ?? 0
                );
            }, 0));
            setCol3(fbStore.targetDataPerCluster.map(c => c.clusterId).reduce((total, clusterId) => {
                return total + (
                    fbStore.contributionPerClusterSavings
                        ?.find(c => c.clusterId === clusterId)  // Find the cluster with clusterId === 3
                        ?.doiData                       // Get the doiData array
                        .filter(doiEntry => doiEntry.doi === 5)  // Find the entry where the doi is 5
                        ?.reduce((sum, doiEntry) => {   // Sum up all December values within this cluster
                            return sum + doiEntry.data
                                .reduce((doiSum, dataPoint) => doiSum + dataPoint.value, 0);
                        }, 0) ?? 0
                );
            }));
            setCol4(fbStore.targetDataPerCluster.map(c => c.clusterId).reduce((total, clusterId) => {
                return total + (
                    fbStore.targetDataPerCluster
                        ?.find(c => c.clusterId === clusterId)
                        ?.data
                        ?.find(d => d.month.getMonth() === 11)
                        ?.value ?? 0
                );
            }, 0));
        }
    }, [fbStore.targetDataPerCluster]);

    return (
        <div className="financial-dashboard-page">
            <FinancialFilters/>

            <div className="sit-fin-grid">
                <div className="sit-card">
                    <div className="sit-cardhead">
                        <h3 className="sit-cardtitle">{fin('cumulatedActualVsTarget')}</h3>
                    </div>
                    <TargetVsActualBar/>
                </div>

                <div className="sit-card">
                    <div className="sit-cardhead">
                        <h3 className="sit-cardtitle">{fin('cumulatedSavingsByCompany')}</h3>
                    </div>
                    <PerCompanyBar/>
                </div>

                <div className="sit-card">
                    <div className="sit-cardhead">
                        <h3 className="sit-cardtitle">{fin('procurementSavingsDevelopment')}</h3>
                    </div>
                    <TargetVsActualDoubleBar
                        actualData={fbStore.contributionPerMonth}
                        targetData={fbStore.cumulatedTargetData}
                        height={280}
                    />
                </div>

                <div className="sit-card">
                    <div className="sit-cardhead">
                        <h3 className="sit-cardtitle">{fin('approvedVsPending')}</h3>
                    </div>
                    <TargetVsActualVertical/>
                </div>

                <div className="sit-card span2">
                    <div className="sit-cardhead">
                        <h3 className="sit-cardtitle">{fin('clusterSummary')}</h3>
                    </div>
                    <TableContainer className="target-vs-actual-table">
                        <Table>
                            <MithraTableHeadColumns columns={columns}/>
                            <TableBody>
                                {fbStore.targetDataPerCluster?.map(cluster_data => <TableRow
                                    key={cluster_data.clusterId}>
                                    <MithraTableCell c={columns[0]}>
                                        {cluster_data.clusterTitle}
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[1]}>
                                        <CurrencyComponent
                                            v={fbStore.contributionPerClusterCumSumSavings?.find(c => c.clusterId === cluster_data.clusterId)?.data.find(dataPoint => dataPoint.month.getMonth() === 11)?.value ?? 0}
                                            nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[2]}>
                                        <CurrencyComponent
                                            v={fbStore.contributionPerClusterCumSumAvoidance?.find(c => c.clusterId === cluster_data.clusterId)?.data.find(dataPoint => dataPoint.month.getMonth() === 11)?.value ?? 0}
                                            nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[3]}>
                                        <CurrencyComponent v={fbStore.contributionPerClusterSavings
                                            ?.find(c => c.clusterId === cluster_data.clusterId)
                                            ?.doiData                       // Get the doiData array
                                            .filter(doiEntry => doiEntry.doi === 5)  // Find the entry where the doi is 5
                                            ?.reduce((sum, doiEntry) => {   // Sum up all December values within this cluster
                                                return sum + doiEntry.data
                                                    .reduce((doiSum, dataPoint) => doiSum + dataPoint.value, 0);
                                            }, 0) ?? 0} nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[4]}>
                                        <CurrencyComponent v={fbStore.targetDataPerCluster
                                            ?.find(c => c.clusterId === cluster_data.clusterId)  // Find the entry where the clusterId is 3
                                            ?.data                          // Get the data array
                                            ?.find(d => d.month.getMonth() === 11) // Find the entry where the month is December (month index is 11)
                                            ?.value ?? 0} nDigits={3}/>
                                    </MithraTableCell>
                                </TableRow>)}
                                <TableRow style={{fontWeight: 'bold'}}>
                                    <MithraTableCell c={columns[0]}>
                                        {fin('grandTotal')}
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[1]}>
                                        <CurrencyComponent v={col_1} nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[2]}>
                                        <CurrencyComponent v={col_2} nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[3]}>
                                        <CurrencyComponent v={col_3} nDigits={3}/>
                                    </MithraTableCell>
                                    <MithraTableCell c={columns[4]}>
                                        <CurrencyComponent v={col_4} nDigits={3}/>
                                    </MithraTableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
});
